import { useModal } from "@ebay/nice-modal-react"
import type { TaskDelegateTarget } from "../persistence/records"
import { DelegateModeControl } from "./DelegateModeControl"
import { Popover } from "@base-ui-components/react/popover"
import { Tooltip } from "@base-ui-components/react/tooltip"
import cx from "classnames"
import {
    ArrowDown,
    Check,
    ChevronDown,
    Clock3,
    Files,
    GitBranch,
    GitCommitHorizontal,
    GitPullRequest,
    ImagePlus,
    Play,
    Route,
    Settings2,
    X,
} from "lucide-react"
import { observer } from "mobx-react"
import { type KeyboardEvent as ReactKeyboardEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { Z_INDEX } from "../constants"
import { useMediaQuery } from "../hooks/useMediaQuery"
import { usePortalContainer } from "../hooks/usePortalContainer"
import { savedActionConfirmationCopy } from "../actions/actionConfirmation"
import { savedActionIcon } from "../actions/actionIcons"
import { ACTION_COLOR_STYLES } from "../actions/actionStyles"
import type { SessionActionTargets } from "../actions/sessionActionTarget"
import type { FsWorkspaceContext } from "../nestAPIs/files"
import type { GitSummaryResponse } from "../nestAPIs/git"
import { openExternalUrl } from "../platform/actions"
import { resolveModelForHarness } from "../modelRegistry"
import type { HarnessId } from "../providerEvents/harnessEventTypes"
import type { ThinkingLevel } from "../store/TaskModel"
import type { NewTaskAgentTarget } from "../persistence/newTaskAgentPins"
import type { Command, InputAction, InputManager } from "../store/managers/InputManager"
import type { SdkCapabilitiesManager } from "../store/managers/SdkCapabilitiesManager"
import type { SmartEditorManager } from "../store/managers/SmartEditorManager"
import type { TrayManager } from "../store/managers/TrayManager"
import type { Comment } from "../types"
import { processImageBlob } from "../utils/imageAttachment"
import { SmartEditor, type SmartEditorRef } from "./SmartEditor"
import { TaskAgentControls } from "./TaskAgentControls"
import { PullRequestAutomationControls } from "./PullRequestAutomationControls"
import type { TaskPullRequestControl } from "./TaskGitControl"
import { PersonGlyph } from "./computers/PersonGlyph"
import { CommentsSection } from "./events/CommentsSection"
import { INPUT_FOCUS_HOTKEYS, MAIN_INPUT_HOTKEY_SCOPE_PROPS } from "./inputHotkeyScope"
import { ModalConfirm } from "./ui"
import { SessionActionPickerModal } from "./actions/SessionActionPickerModal"

const COMPACT_INPUT_BAR_QUERY = "(max-width: 719px)"
const WORK_DISPATCH_COMMAND_IDS = new Set(["do", "queue", "steer", "interrupt"])
const relativeTimeFormatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" })
const exactTimeFormatter = new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" })

// Button variant styles
const BUTTON_BASE = "btn flex items-center h-9 text-sm font-medium transition-all duration-100 whitespace-nowrap shrink-0"
const BUTTON_LABELED = `${BUTTON_BASE} justify-center gap-2 px-4`
// Semantic button styles - each variant has enabled and disabled states
// Disabled states preserve the button's color identity with reduced opacity
const BUTTON_STYLES = {
    primary: {
        enabled: "bg-primary text-primary-content cursor-pointer hover:bg-primary/80 active:bg-primary/70 active:scale-95",
        disabled: "bg-primary/40 text-primary-content/50 cursor-not-allowed",
    },
    success: {
        enabled: "bg-success text-success-content cursor-pointer hover:bg-success/80 active:bg-success/70 active:scale-95",
        disabled: "bg-success/40 text-success-content/50 cursor-not-allowed",
    },
    danger: {
        enabled: "bg-error text-error-content cursor-pointer hover:bg-error/80 active:bg-error/70 active:scale-95",
        disabled: "bg-error/40 text-error-content/50 cursor-not-allowed",
    },
    neutral: {
        enabled: "bg-base-200 text-base-content cursor-pointer hover:bg-base-300 active:bg-base-300 active:scale-95",
        disabled: "bg-base-200/40 text-base-content/50 cursor-not-allowed",
    },
    ghost: {
        enabled: "text-base-content cursor-pointer hover:bg-base-200 active:bg-base-300 active:scale-95",
        disabled: "text-muted/50 cursor-not-allowed",
    },
} as const

type ButtonVariant = keyof typeof BUTTON_STYLES

function isSubmitShortcut(event: ReactKeyboardEvent): boolean {
    return event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey
}

function actionMosaicColumns(actionCount: number): string {
    const columnCount = Math.ceil(actionCount / 3)
    return columnCount > 0 ? `repeat(${columnCount}, minmax(7rem, 1fr))` : "none"
}

function scheduledTimeLabel(at: string, now: number): string {
    const deltaMs = Date.parse(at) - now
    if (deltaMs <= 0) return "Due"
    if (deltaMs < 60 * 1000) return "soon"
    const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
        ["week", 7 * 24 * 60 * 60 * 1000],
        ["day", 24 * 60 * 60 * 1000],
        ["hour", 60 * 60 * 1000],
        ["minute", 60 * 1000],
    ]
    for (const [unit, unitMs] of units) {
        if (deltaMs >= unitMs) return relativeTimeFormatter.format(Math.ceil(deltaMs / unitMs), unit)
    }
    return "soon"
}

function scheduledStateLabel(state: InputManager["scheduledTurns"][number]["state"], at: string, now: number): string {
    if (state === "due_waiting") return "Due · waiting for current work"
    if (state === "dispatching") return "Starting"
    if (state === "failed" || state === "parked") return "Needs attention"
    return scheduledTimeLabel(at, now)
}

function ComposerGitStatus({
    gitStatus,
    taskCommitCount,
    onOpen,
}: {
    gitStatus: GitSummaryResponse
    taskCommitCount?: number | null
    onOpen: () => void
}): JSX.Element {
    const { filesChanged, insertions, deletions, unknownFiles = 0 } = gitStatus.total
    const branch = gitStatus.branch ?? `Detached at ${gitStatus.headCommit.slice(0, 7)}`
    const lineTotalsUnavailable = gitStatus.hasChanges && insertions === 0 && deletions === 0
    const clean = !gitStatus.hasChanges && filesChanged === 0
    const details = [
        branch,
        taskCommitCount !== undefined && taskCommitCount !== null && taskCommitCount > 0 ? `${taskCommitCount} commits from base` : null,
        clean ? "clean working tree" : `${filesChanged} changed ${filesChanged === 1 ? "file" : "files"}`,
        lineTotalsUnavailable ? "line totals unavailable" : `${insertions} additions, ${deletions} deletions`,
        unknownFiles > 0 ? `${unknownFiles} files with unknown line totals` : null,
    ].filter((detail): detail is string => detail !== null)

    return (
        <button
            type="button"
            className="btn flex h-7 shrink-0 items-center gap-1.5 px-2 font-mono text-[10px] tabular-nums text-muted transition-colors hover:bg-base-200 hover:text-base-content"
            onClick={onOpen}
            aria-label={`Open Git changes: ${details.join(", ")}`}
            title={`${details.join(" · ")}\nOpen Git changes`}
        >
            <GitBranch size={12} className="shrink-0" />
            <span className="max-w-24 truncate text-xs">{branch}</span>
            {taskCommitCount !== undefined && taskCommitCount !== null && taskCommitCount > 0 && (
                <span className="flex items-center gap-0.5" title={`${taskCommitCount} commits from base`}>
                    <GitCommitHorizontal size={10} />
                    {taskCommitCount}
                </span>
            )}
            {clean ? (
                <span>clean</span>
            ) : (
                <>
                    <span className="flex items-center gap-0.5" title={`${filesChanged} changed files`}>
                        <Files size={10} />
                        {filesChanged}
                    </span>
                    {lineTotalsUnavailable ? (
                        <span className="text-primary">dirty</span>
                    ) : (
                        <span className="flex items-center gap-1">
                            {insertions > 0 && <span className="text-success">+{insertions}</span>}
                            {deletions > 0 && <span className="text-error">−{deletions}</span>}
                            {unknownFiles > 0 && <span>+?</span>}
                        </span>
                    )}
                </>
            )}
        </button>
    )
}

/** Reusable button for the small, fixed set of transport commands. */
function CommandButton({
    command,
    onExecute,
    portalContainer,
    menu = false,
}: {
    command: Command
    onExecute: (id: string) => void
    portalContainer: HTMLElement | null
    menu?: boolean
}) {
    const Icon = command.icon
    const variant = (command.style?.variant ?? "ghost") as ButtonVariant
    const styles = BUTTON_STYLES[variant]
    const tooltipTitle = command.tooltip ? `${command.label}: ${command.tooltip}` : command.label

    const btn = (
        <button
            type="button"
            onClick={() => onExecute(command.id)}
            disabled={!command.enabled}
            className={cx(
                menu ? `${BUTTON_BASE} w-full justify-start gap-2 px-4` : BUTTON_LABELED,
                "relative",
                command.enabled ? styles.enabled : styles.disabled
            )}
            title={tooltipTitle}
        >
            <Icon size={14} />
            {command.label}
        </button>
    )

    if (!command.tooltip) return btn

    return (
        <Tooltip.Root delay={0}>
            <Tooltip.Trigger render={btn} />
            <Tooltip.Portal container={portalContainer}>
                <Tooltip.Positioner sideOffset={6} side="top">
                    <Tooltip.Popup className="max-w-64 bg-base-300 text-base-content text-xs px-2 py-1 shadow-lg border border-border">
                        {tooltipTitle}
                    </Tooltip.Popup>
                </Tooltip.Positioner>
            </Tooltip.Portal>
        </Tooltip.Root>
    )
}

function ActionButton({
    action,
    onExecute,
    menu = false,
    receivedCount = 0,
}: {
    action: InputAction
    onExecute: (id: string) => void
    menu?: boolean
    receivedCount?: number
}) {
    const Icon = savedActionIcon(action.icon)
    const title = !action.isAvailable
        ? `${action.name} (constraints not met)`
        : action.requiresConfirmation
          ? `${action.name} (confirmation required)`
          : action.name
    return (
        <button
            type="button"
            disabled={!action.isAvailable}
            className={cx(
                menu
                    ? "btn flex h-10 min-w-0 shrink-0 items-center justify-start gap-1.5 px-3 text-sm font-semibold transition-all active:scale-95"
                    : "btn flex h-8 min-w-0 shrink-0 items-center gap-1 px-1 text-xs font-semibold transition-all active:scale-95",
                ACTION_COLOR_STYLES[action.color].button,
                !action.isAvailable && "cursor-not-allowed opacity-40 saturate-50 active:scale-100"
            )}
            onClick={() => onExecute(action.id)}
            title={title}
            aria-label={receivedCount > 0 ? `${action.name}, received${receivedCount > 1 ? ` ${receivedCount} times` : ""}` : undefined}
        >
            <Icon size={13} className="shrink-0" />
            <span className="min-w-0 truncate">{action.name}</span>
            {receivedCount > 0 && (
                <span className="ml-auto flex shrink-0 items-center gap-0.5" data-action-received={receivedCount}>
                    <Check size={13} aria-hidden="true" />
                    {receivedCount > 1 && <span className="text-[10px] tabular-nums">{receivedCount}</span>}
                </span>
            )}
        </button>
    )
}

function CompactActionPicker({
    actions,
    onExecute,
    onManageActions,
    portalContainer,
}: {
    actions: readonly InputAction[]
    onExecute: (id: string) => void
    onManageActions?: () => void
    portalContainer: HTMLElement | null
}) {
    const [open, setOpen] = useState(false)
    const [receivedCounts, setReceivedCounts] = useState<Map<string, number>>(() => new Map())
    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen)
        if (!nextOpen) setReceivedCounts(new Map())
    }

    return (
        <Popover.Root open={open} onOpenChange={handleOpenChange}>
            <Popover.Trigger
                className="btn flex h-9 shrink-0 items-center justify-center gap-2 border border-border bg-base-100 px-3 text-sm font-medium text-base-content transition-colors hover:bg-base-200"
                aria-label="Choose an Action"
                title="Choose an Action"
            >
                <span>Actions</span>
                <ChevronDown size={14} />
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side="top" align="start" sideOffset={8} collisionPadding={8} className="z-50">
                    <Popover.Popup className="w-[min(28rem,calc(100vw-1rem))] border border-border bg-base-100 shadow-lg outline-none" aria-label="Actions">
                        {actions.length > 0 && (
                            <div className="grid max-h-[min(30rem,65dvh)] grid-cols-2 gap-1.5 overflow-y-auto p-2">
                                {actions.map((action) => (
                                    <ActionButton
                                        key={action.id}
                                        action={action}
                                        menu
                                        receivedCount={receivedCounts.get(action.id)}
                                        onExecute={(id) => {
                                            onExecute(id)
                                            setReceivedCounts((current) => {
                                                const next = new Map(current)
                                                next.set(id, (next.get(id) ?? 0) + 1)
                                                return next
                                            })
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="flex border-t border-border">
                            {onManageActions && (
                                <button
                                    type="button"
                                    className="btn flex min-h-11 min-w-0 flex-1 items-center gap-2 px-3 text-sm font-medium text-muted transition-colors hover:bg-base-200 hover:text-base-content"
                                    onClick={() => {
                                        handleOpenChange(false)
                                        onManageActions()
                                    }}
                                >
                                    <Settings2 size={14} />
                                    Manage Actions
                                </button>
                            )}
                            <button
                                type="button"
                                className={cx(
                                    "btn ml-auto flex min-h-11 items-center justify-center px-4 text-sm font-semibold text-base-content transition-colors hover:bg-base-200",
                                    onManageActions && "border-l border-border"
                                )}
                                onClick={() => handleOpenChange(false)}
                                aria-label="Done choosing Actions"
                            >
                                Done
                            </button>
                        </div>
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
}

function CompactCommandPicker({
    commands,
    primaryCommandId,
    onExecute,
    portalContainer,
}: {
    commands: readonly Command[]
    primaryCommandId: string
    onExecute: (id: string) => void
    portalContainer: HTMLElement | null
}) {
    const [open, setOpen] = useState(false)
    const submitCommand = commands.find((command) => command.id === primaryCommandId)
    const primaryCommand = (submitCommand?.enabled ? submitCommand : commands.find((command) => command.enabled)) ?? submitCommand ?? commands[0]
    if (!primaryCommand) return null

    if (commands.length === 1) {
        return <CommandButton command={primaryCommand} portalContainer={portalContainer} onExecute={onExecute} />
    }

    const Icon = primaryCommand.icon
    const variant = (primaryCommand.style?.variant ?? "ghost") as ButtonVariant
    const styles = BUTTON_STYLES[variant]
    const hasEnabledCommand = commands.some((command) => command.enabled)

    return (
        <Popover.Root open={open} onOpenChange={setOpen}>
            <Popover.Trigger
                className={cx(BUTTON_LABELED, "border border-transparent px-3", hasEnabledCommand ? styles.enabled : styles.disabled)}
                aria-label="Choose how to send"
                title="Choose how to send"
                disabled={!hasEnabledCommand}
            >
                <Icon size={14} />
                <span>{primaryCommand.label}</span>
                <ChevronDown size={14} />
            </Popover.Trigger>
            <Popover.Portal container={portalContainer}>
                <Popover.Positioner side="top" align="start" sideOffset={8} collisionPadding={8} className="z-50">
                    <Popover.Popup
                        className="flex w-[min(20rem,calc(100vw-1rem))] flex-col gap-1.5 border border-border bg-base-100 p-2 shadow-lg outline-none"
                        aria-label="Send options"
                    >
                        {commands.map((command) => (
                            <CommandButton
                                key={command.id}
                                command={command}
                                menu
                                portalContainer={portalContainer}
                                onExecute={(id) => {
                                    setOpen(false)
                                    onExecute(id)
                                }}
                            />
                        ))}
                    </Popover.Popup>
                </Popover.Positioner>
            </Popover.Portal>
        </Popover.Root>
    )
}

export const InputBar = observer(function InputBar({
    input,
    editorManager,
    tray,
    fileMentionsDir,
    fileMentionsContext,
    slashCommandsDir,
    sdkCapabilities,
    unsubmittedComments = [],
    onDeleteComment,
    gitStatus,
    taskCommitCount,
    pullRequest,
    selectedModel,
    onModelChange,
    delegateTarget,
    onDelegateChange,
    thinking,
    onThinkingChange,
    fastMode,
    onFastModeChange,
    harnessId,
    pinnedAgentTargets,
    autoFocusKey,
    onManageActions,
    hideTray = false,
    showScrollToBottom = false,
    onScrollToBottom,
    taskHandoffControl,
    taskLifecycleControl,
    onComposerInput,
    onStopRequested,
    onWorkDispatchStarted,
    onWorkDispatched,
}: {
    input: InputManager
    editorManager: SmartEditorManager
    tray: TrayManager
    /** Directory for @file mention autocomplete, null to disable */
    fileMentionsDir: string | null
    fileMentionsContext?: FsWorkspaceContext | null
    /** Directory for /slash command autocomplete, null to disable */
    slashCommandsDir: string | null
    /** SDK capabilities manager for slash command discovery */
    sdkCapabilities?: SdkCapabilitiesManager
    unsubmittedComments?: Comment[]
    onDeleteComment?: (commentId: string) => Promise<void> | void
    gitStatus?: GitSummaryResponse | null
    taskCommitCount?: number | null
    pullRequest?: TaskPullRequestControl
    selectedModel?: string
    onModelChange?: (model: string) => void
    delegateTarget?: TaskDelegateTarget | null
    onDelegateChange?: (target: TaskDelegateTarget | null) => Promise<void>
    thinking?: ThinkingLevel
    onThinkingChange?: (level: ThinkingLevel) => void
    fastMode?: boolean
    onFastModeChange?: (enabled: boolean) => void
    harnessId?: HarnessId
    pinnedAgentTargets?: readonly NewTaskAgentTarget[]
    /** Dev: Hide tray buttons and slide-out */
    hideTray?: boolean
    autoFocusKey?: string
    onManageActions?: () => void
    showScrollToBottom?: boolean
    onScrollToBottom?: () => void
    taskHandoffControl?: ReactNode
    taskLifecycleControl?: ReactNode
    onComposerInput?: () => void
    onStopRequested?: () => void
    onWorkDispatchStarted?: () => void
    onWorkDispatched?: () => void
}) {
    const portalContainer = usePortalContainer()
    const actionConfirm = useModal(ModalConfirm)
    const sessionActionPicker = useModal(SessionActionPickerModal)
    const editorRef = useRef<SmartEditorRef>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const compact = useMediaQuery(COMPACT_INPUT_BAR_QUERY)
    const [scheduleNow, setScheduleNow] = useState(Date.now())
    const [runningScheduleIds, setRunningScheduleIds] = useState<Set<string>>(() => new Set())
    const [actionError, setActionError] = useState<{ input: InputManager; message: string } | null>(null)

    // Get commands from InputManager
    const { actions, commands } = input
    const primarySubmitCommandId = commands.some((command) => command.id === "queue") ? "queue" : "do"
    const hasComments = unsubmittedComments.length > 0
    const hasPendingImages = editorManager.pendingImages.length > 0
    const queuedTurns = input.queuedTurns
    const scheduledTurns = input.scheduledTurns
    const pendingProposals = input.pendingProposals
    const showAgentControls = Boolean(selectedModel && onModelChange && thinking && onThinkingChange && fastMode !== undefined && onFastModeChange && harnessId)
    const [resolvingProposalId, setResolvingProposalId] = useState<string | null>(null)
    const resolveProposal = useCallback(
        async (proposalId: string, outcome: "approved" | "declined") => {
            setResolvingProposalId(proposalId)
            // Approving runs the suggestion as this client's own turn, so it
            // counts as dispatched work for Q mode exactly like Do or Queue.
            if (outcome === "approved") onWorkDispatchStarted?.()
            try {
                if (outcome === "approved") {
                    await input.approveProposal(proposalId)
                    onWorkDispatched?.()
                } else {
                    await input.declineProposal(proposalId)
                }
            } catch (error) {
                console.error("[InputBar] Failed to resolve suggestion", error)
            } finally {
                setResolvingProposalId(null)
            }
        },
        [input, onWorkDispatchStarted, onWorkDispatched]
    )
    useEffect(() => {
        if (scheduledTurns.length === 0) return
        setScheduleNow(Date.now())
        const interval = window.setInterval(() => setScheduleNow(Date.now()), 60_000)
        return () => window.clearInterval(interval)
    }, [scheduledTurns.length])
    const runCommand = useCallback(
        async (id: string) => {
            const command = commands.find((cmd) => cmd.id === id)
            if (!command?.enabled) return

            if (tray.isModalOpen) tray.close()
            if (id === "stop") onStopRequested?.()
            if (WORK_DISPATCH_COMMAND_IDS.has(id)) onWorkDispatchStarted?.()
            await input.runCommand(id)
            if (WORK_DISPATCH_COMMAND_IDS.has(id)) onWorkDispatched?.()
        },
        [commands, input, onStopRequested, onWorkDispatchStarted, onWorkDispatched, tray]
    )
    const runScheduledTurn = useCallback(
        async (schedule: InputManager["scheduledTurns"][number]) => {
            setRunningScheduleIds((current) => new Set(current).add(schedule.scheduleId))
            try {
                onWorkDispatchStarted?.()
                await input.runScheduledTurn(schedule)
                onWorkDispatched?.()
            } catch (error) {
                console.error("[InputBar] Failed to run scheduled follow-up", error)
            } finally {
                setRunningScheduleIds((current) => {
                    const next = new Set(current)
                    next.delete(schedule.scheduleId)
                    return next
                })
            }
        },
        [input, onWorkDispatchStarted, onWorkDispatched]
    )
    const steerQueuedTurn = useCallback(
        async (queuedTurnId: string) => {
            onWorkDispatchStarted?.()
            try {
                const dispatched = await input.steerQueuedTurn(queuedTurnId)
                if (dispatched) onWorkDispatched?.()
            } catch (error) {
                console.error("[InputBar] Failed to steer queued message", error)
            }
        },
        [input, onWorkDispatchStarted, onWorkDispatched]
    )
    const loadSessionActionHarnesses = useCallback(() => input.listSessionActionHarnesses(), [input])
    const executeAction = useCallback(
        async (id: string, confirmed: boolean, sessionTargets?: SessionActionTargets) => {
            setActionError(null)
            if (tray.isModalOpen) tray.close()
            onWorkDispatchStarted?.()
            try {
                await input.runSavedAction(id, { confirmed, sessionTargets })
                onWorkDispatched?.()
            } catch (error) {
                setActionError({ input, message: error instanceof Error ? error.message : "The Action could not be started." })
            }
        },
        [input, onWorkDispatchStarted, onWorkDispatched, tray]
    )
    const confirmAndExecuteAction = useCallback(
        (action: InputAction, sessionTargets?: SessionActionTargets) => {
            if (!action.requiresConfirmation) {
                void executeAction(action.id, false, sessionTargets)
                return
            }

            const confirmationCopy = savedActionConfirmationCopy(action, editorManager.hasDraftableContent)
            actionConfirm.show({
                ...confirmationCopy,
                onConfirm: () => void executeAction(action.id, true, sessionTargets),
            })
        },
        [actionConfirm, editorManager.hasDraftableContent, executeAction]
    )
    const runAction = useCallback(
        (id: string) => {
            const action = actions.find((candidate) => candidate.id === id)
            if (!action?.isAvailable) return
            if (!action.sessionLaunch) {
                confirmAndExecuteAction(action)
                return
            }

            void sessionActionPicker.show({
                actionName: action.name,
                actionKind: action.sessionLaunch.kind,
                description: action.sessionLaunch.description,
                ...(action.sessionLaunch.maxTargets === undefined ? {} : { maxTargets: action.sessionLaunch.maxTargets }),
                pinnedTargets: pinnedAgentTargets,
                ...(harnessId && selectedModel
                    ? {
                          currentTarget: {
                              thinking,
                              harnessId,
                              modelId: resolveModelForHarness(selectedModel, harnessId),
                          },
                      }
                    : {}),
                loadHarnesses: loadSessionActionHarnesses,
                onSelect: (targets) => confirmAndExecuteAction(action, targets),
            })
        },
        [actions, confirmAndExecuteAction, harnessId, loadSessionActionHarnesses, pinnedAgentTargets, selectedModel, sessionActionPicker]
    )
    const handleEditorKeyDown = useCallback(
        (event: ReactKeyboardEvent) => {
            if (isSubmitShortcut(event)) {
                event.preventDefault()
                void runCommand(primarySubmitCommandId)
                return
            }

            if (event.key !== "Escape" || tray.isOpen) return

            event.preventDefault()
            editorRef.current?.blur()
        },
        [primarySubmitCommandId, runCommand, tray.isOpen]
    )

    useHotkeys(
        INPUT_FOCUS_HOTKEYS,
        (event) => {
            event.preventDefault()
            editorRef.current?.focusEnd()
        },
        {
            enableOnContentEditable: true,
            enableOnFormTags: ["INPUT", "TEXTAREA", "SELECT"],
        },
        []
    )

    useEffect(() => {
        if (autoFocusKey === undefined) return

        const frame = requestAnimationFrame(() => {
            editorRef.current?.focusEnd()
        })

        return () => cancelAnimationFrame(frame)
    }, [autoFocusKey])

    return (
        <div className="absolute bottom-6 left-1/2 w-full max-w-3xl -translate-x-1/2 px-4" style={{ zIndex: Z_INDEX.INPUT_BAR }}>
            {showScrollToBottom && onScrollToBottom && (
                <div className="pointer-events-none absolute -top-11 left-0 flex w-full justify-center">
                    <button
                        type="button"
                        className="btn pointer-events-auto flex h-8 items-center gap-1.5 border border-border bg-base-100 px-2.5 text-xs text-base-content shadow-md hover:bg-base-200"
                        onClick={onScrollToBottom}
                        title="Scroll to latest message"
                        aria-label="Scroll to latest message"
                    >
                        <ArrowDown size={13} />
                        <span>Latest</span>
                    </button>
                </div>
            )}
            <div className="bg-base-100 border border-border shadow-lg">
                {(showAgentControls || (!hideTray && gitStatus) || pullRequest || input.pullRequestAutomation) && (
                    <div
                        className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-border bg-base-100 px-1 py-1"
                        data-task-composer-status="true"
                    >
                        <div className="flex min-w-0 flex-wrap items-center gap-1">
                            {showAgentControls &&
                                selectedModel &&
                                onModelChange &&
                                thinking &&
                                onThinkingChange &&
                                fastMode !== undefined &&
                                onFastModeChange &&
                                harnessId && (
                                    <TaskAgentControls
                                        selectedModel={selectedModel}
                                        onModelChange={onModelChange}
                                        thinking={thinking ?? "max"}
                                        onThinkingChange={onThinkingChange}
                                        fastMode={fastMode}
                                        onFastModeChange={onFastModeChange}
                                        harnessId={harnessId}
                                        side="top"
                                        align="start"
                                    />
                                )}
                            {onDelegateChange && harnessId && selectedModel && (
                                <DelegateModeControl
                                    thinking={thinking ?? "max"}
                                    value={delegateTarget ?? null}
                                    onChange={onDelegateChange}
                                    harnessId={harnessId}
                                    modelId={selectedModel}
                                    pinnedTargets={pinnedAgentTargets}
                                    loadHarnesses={loadSessionActionHarnesses}
                                />
                            )}
                        </div>
                        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-1">
                            {!hideTray && gitStatus && (
                                <ComposerGitStatus gitStatus={gitStatus} taskCommitCount={taskCommitCount} onOpen={() => tray.open("gitlog")} />
                            )}
                            <span className="inline-flex shrink-0 items-center gap-0.5">
                                {pullRequest && (
                                    <button
                                        type="button"
                                        className="btn flex h-7 shrink-0 items-center gap-1.5 px-2 text-xs font-medium text-primary transition-colors hover:bg-base-200"
                                        onClick={() => void openExternalUrl(pullRequest.url)}
                                        aria-label={`Open ${pullRequest.provider === "gitlab" ? "MR" : "PR"}${pullRequest.number ? ` #${pullRequest.number}` : ""}`}
                                        title={pullRequest.url}
                                    >
                                        <GitPullRequest size={12} />
                                        <span>
                                            {pullRequest.provider === "gitlab" ? "MR" : "PR"}
                                            {pullRequest.number ? ` #${pullRequest.number}` : ""}
                                        </span>
                                    </button>
                                )}
                                {(input.pullRequestAutomation || (pullRequest?.provider === "github" && pullRequest.watch === "active")) && (
                                    <PullRequestAutomationControls
                                        key={tray.taskId}
                                        automation={input.pullRequestAutomation}
                                        disabledReason={input.pullRequestAutomationDisabledReason}
                                        onChange={(enabled) => input.setWatchPR(enabled)}
                                        onRetry={() => input.retryPullRequestAutomation()}
                                    />
                                )}
                            </span>
                        </div>
                    </div>
                )}
                {/* Pending comments section */}
                {hasComments && <CommentsSection comments={unsubmittedComments} variant="pending" onDeleteComment={onDeleteComment} />}

                {scheduledTurns.length > 0 && (
                    <div className="border-t border-border bg-base-100 px-2 py-2">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted">Scheduled</div>
                        <div className="flex flex-col gap-1">
                            {scheduledTurns.map((schedule) => {
                                const needsAttention = schedule.state === "failed" || schedule.state === "parked"
                                const isStarting = schedule.state === "dispatching" || runningScheduleIds.has(schedule.scheduleId)
                                return (
                                    <div
                                        key={schedule.scheduleId}
                                        className={cx(
                                            "flex min-w-0 items-center gap-2 border bg-base-200 px-2 py-1.5 text-xs",
                                            needsAttention ? "border-error/60" : "border-border"
                                        )}
                                    >
                                        <Clock3 size={12} className={cx("shrink-0", needsAttention ? "text-error" : "text-primary")} />
                                        <span className={cx("shrink-0 font-medium", needsAttention ? "text-error" : "text-primary")}>
                                            {scheduledStateLabel(schedule.state, schedule.at, scheduleNow)}
                                        </span>
                                        <span className="min-w-0 flex-1 truncate text-base-content" title={schedule.instructions}>
                                            {schedule.note || schedule.instructions}
                                        </span>
                                        <span className="hidden shrink-0 text-muted sm:inline" title={schedule.at}>
                                            {exactTimeFormatter.format(new Date(schedule.at))}
                                        </span>
                                        {schedule.error && (
                                            <span className="max-w-40 shrink truncate text-error" title={schedule.error}>
                                                {schedule.error}
                                            </span>
                                        )}
                                        <button
                                            type="button"
                                            disabled={isStarting}
                                            className={cx(
                                                "btn flex shrink-0 items-center gap-1 border border-primary/40 px-2 py-1 font-medium text-primary",
                                                isStarting ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-primary/10 active:bg-primary/20"
                                            )}
                                            onClick={() => void runScheduledTurn(schedule)}
                                            aria-label="Run scheduled follow-up now"
                                            title={isStarting ? "Follow-up is starting" : "Run scheduled follow-up now"}
                                        >
                                            <Play size={11} />
                                            {isStarting ? "Starting" : "Run now"}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={isStarting}
                                            className={cx(
                                                "btn shrink-0 p-1 text-muted",
                                                isStarting ? "cursor-not-allowed opacity-40" : "cursor-pointer hover:bg-base-300 hover:text-base-content"
                                            )}
                                            onClick={() =>
                                                void input
                                                    .cancelScheduledTurn(schedule)
                                                    .catch((error) => console.error("[InputBar] Failed to cancel scheduled follow-up", error))
                                            }
                                            aria-label="Cancel scheduled follow-up"
                                            title={isStarting ? "Follow-up is starting" : "Cancel scheduled follow-up"}
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                )}

                {pendingProposals.length > 0 && (
                    <div className="border-t border-border bg-base-100 px-2 py-2" data-task-proposals="true">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted">Suggestions</div>
                        <div className="flex flex-col gap-1">
                            {pendingProposals.map((proposal) => (
                                <div key={proposal.id} className="flex min-w-0 items-center gap-2 border border-primary/30 bg-primary/5 px-2 py-1.5 text-xs">
                                    <PersonGlyph name={proposal.by.displayName} glyph={proposal.by.glyph} color={proposal.by.color} size="sm" />
                                    <span className="shrink-0 font-medium text-primary">{proposal.by.displayName} suggests</span>
                                    <span className="min-w-0 flex-1 truncate text-base-content" title={proposal.input}>
                                        {proposal.input}
                                    </span>
                                    <button
                                        type="button"
                                        className="btn shrink-0 cursor-pointer px-1.5 py-0.5 text-[11px] font-medium text-success hover:bg-success/10 disabled:opacity-50"
                                        onClick={() => void resolveProposal(proposal.id, "approved")}
                                        disabled={resolvingProposalId !== null}
                                        aria-label={`Approve suggestion from ${proposal.by.displayName}`}
                                        title="Run this suggestion as the next turn"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        className="btn shrink-0 cursor-pointer p-1 text-muted hover:bg-base-300 hover:text-base-content disabled:opacity-50"
                                        onClick={() => void resolveProposal(proposal.id, "declined")}
                                        disabled={resolvingProposalId !== null}
                                        aria-label={`Decline suggestion from ${proposal.by.displayName}`}
                                        title="Decline this suggestion"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {queuedTurns.length > 0 && (
                    <div className="border-t border-border bg-base-100 px-2 py-2">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted">Queued</div>
                        <div className="flex flex-col gap-1">
                            {queuedTurns.map((turn) => (
                                <div key={turn.id} className="flex min-w-0 items-center gap-2 border border-border bg-base-200 px-2 py-1.5 text-xs">
                                    <span className="shrink-0 font-medium text-primary">{turn.label ?? "Queued"}</span>
                                    <span className="min-w-0 flex-1 truncate text-base-content" title={turn.input}>
                                        {turn.input || "No message"}
                                    </span>
                                    {turn.note && (
                                        <span className="max-w-32 shrink truncate text-muted" title={turn.note}>
                                            {turn.note}
                                        </span>
                                    )}
                                    {input.canSteerQueuedTurns && (
                                        <button
                                            type="button"
                                            className="btn shrink-0 cursor-pointer p-1 text-muted hover:bg-primary/10 hover:text-primary"
                                            onClick={() => void steerQueuedTurn(turn.id)}
                                            aria-label="Steer queued message"
                                            title="Steer this queued message into the active turn"
                                        >
                                            <Route size={12} />
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn shrink-0 cursor-pointer p-1 text-muted hover:bg-base-300 hover:text-base-content"
                                        onClick={() => void input.cancelQueuedTurn(turn.id)}
                                        aria-label="Cancel queued message"
                                        title="Cancel queued message"
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="group/input-actions" data-manage-actions-hover-area="true">
                    {/* Text input area with image attach overlay */}
                    <div className="relative" {...MAIN_INPUT_HOTKEY_SCOPE_PROPS}>
                        <SmartEditor
                            key={`${editorManager.workspaceId}:${editorManager.id}`}
                            ref={editorRef}
                            manager={editorManager}
                            fileMentionsDir={fileMentionsDir}
                            fileMentionsContext={fileMentionsContext}
                            slashCommandsDir={slashCommandsDir}
                            sdkCapabilities={sdkCapabilities}
                            onKeyDown={handleEditorKeyDown}
                            onUserInput={onComposerInput}
                            placeholder="What would you like to do?"
                            className="min-h-[58px] max-h-[150px] overflow-y-auto text-sm leading-[20px] border-x-0 border-y-0 focus-within:border-border"
                            editorClassName="px-2.5 py-[9px]"
                        />
                        {/* Image attach button - bottom right of textarea */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            className="hidden"
                            onChange={(e) => {
                                const file = e.target.files?.[0]
                                if (file) {
                                    processImageBlob(file)
                                        .then(({ attachment, dataUrl }) => editorManager.addImage(attachment, dataUrl))
                                        .catch((err) => console.error("[InputBar] Failed to process image:", err))
                                    e.target.value = ""
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="btn absolute bottom-1.5 right-1.5 p-1.5 text-muted hover:text-base-content hover:bg-base-300/50 transition-colors"
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach image"
                        >
                            <ImagePlus size={14} />
                        </button>
                    </div>

                    {/* Image preview strip */}
                    {hasPendingImages && (
                        <div className="flex gap-2 px-3 py-2 border-t border-border overflow-x-auto">
                            {editorManager.pendingImages.map((img) => (
                                <div key={img.id} className="relative shrink-0 group">
                                    <img
                                        src={editorManager.pendingImageDataUrls.get(img.id)}
                                        alt=""
                                        className="h-20 object-cover"
                                        style={{
                                            aspectRatio: `${img.resizedWidth}/${img.resizedHeight}`,
                                        }}
                                    />
                                    <button
                                        type="button"
                                        className="btn absolute -top-1.5 -right-1.5 bg-base-300 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                                        onClick={() => editorManager.removeImage(img.id)}
                                    >
                                        <X size={12} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {actionError?.input === input && (
                        <p className="border-t border-border px-2 py-2 text-xs text-warning" role="alert">
                            {actionError.message}
                        </p>
                    )}
                    {!compact && (actions.length > 0 || onManageActions) && (
                        <div className={cx("relative border-t border-border bg-base-100 px-2 py-2", actions.length === 0 && "min-h-10")}>
                            <div
                                className="grid min-w-0 grid-flow-col grid-rows-3 gap-1.5 overflow-x-auto"
                                data-action-mosaic="true"
                                style={{ gridTemplateColumns: actionMosaicColumns(actions.length) }}
                            >
                                {actions.map((action) => (
                                    <ActionButton
                                        key={action.id}
                                        action={action}
                                        onExecute={(id) => {
                                            void runAction(id)
                                        }}
                                    />
                                ))}
                            </div>
                            {onManageActions && (
                                <button
                                    type="button"
                                    className="btn invisible absolute bottom-0 right-0 z-10 flex h-6 w-6 items-center justify-center border-l border-t border-border bg-base-100 text-muted opacity-0 group-hover/input-actions:visible group-hover/input-actions:opacity-100 hover:bg-base-200 hover:text-base-content"
                                    onClick={onManageActions}
                                    title="Manage Actions"
                                    aria-label="Manage Actions"
                                >
                                    <Settings2 size={11} />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Transport controls */}
                <Tooltip.Provider>
                    <div className="flex flex-wrap items-center gap-2 px-2 py-2 bg-base-200">
                        {compact ? (
                            <>
                                {(actions.length > 0 || onManageActions) && (
                                    <CompactActionPicker
                                        key={`compact-actions:${tray.taskId}`}
                                        actions={actions}
                                        onManageActions={onManageActions}
                                        portalContainer={portalContainer}
                                        onExecute={(id) => {
                                            void runAction(id)
                                        }}
                                    />
                                )}
                                <CompactCommandPicker
                                    key={`compact-commands:${tray.taskId}`}
                                    commands={commands}
                                    primaryCommandId={primarySubmitCommandId}
                                    portalContainer={portalContainer}
                                    onExecute={(id) => {
                                        void runCommand(id)
                                    }}
                                />
                            </>
                        ) : (
                            commands.map((cmd) => (
                                <div key={cmd.id} className={cx("flex items-center gap-1.5", cmd.spacer && "ml-auto")}>
                                    <CommandButton
                                        command={cmd}
                                        portalContainer={portalContainer}
                                        onExecute={(id) => {
                                            void runCommand(id)
                                        }}
                                    />
                                </div>
                            ))
                        )}
                        {(taskHandoffControl || taskLifecycleControl) && (
                            <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-2" data-task-lifecycle-control="true">
                                {taskHandoffControl}
                                {taskLifecycleControl}
                            </div>
                        )}
                    </div>
                </Tooltip.Provider>
            </div>
        </div>
    )
})
